var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
/**
 * CZNA-972 — the review queue over proposals and published records.
 *
 * CZNA-1446 — two views of it: the Inbox (`mode="inbox"`, everything waiting on the caller,
 * `GET /review-queue/returned`) and the administrator's "Všechny kontroly" (`mode="all"`,
 * the whole review, `GET /review-queue`).
 *
 * CZNA-1644 — both narrow by kind ("Druh", kept in the URL as `?kind=…`) and by sigla
 * (remembered per user); each facet counts under the other one's selection.
 */
/* eslint-disable jsx-a11y/no-noninteractive-tabindex -- The native horizontal scroll region
   must be keyboard-focusable; it is labelled below and contains the real table widget. */
import React, { useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { useQuery } from 'react-query';
import { useOkapiKy, useStripes } from '@folio/stripes/core';
import { Accordion, AccordionSet, Button, Icon, Layout, MessageBanner, MultiColumnList, Pane, PaneMenu, Paneset, Select, } from '@folio/stripes/components';
import { fetchReturnedQueue, fetchReviewQueue, fetchReviewQueueKinds, fetchReviewQueueSiglas, } from '../api/reviewQueueService';
import { ReviewBadge } from '../components/ReviewBadge';
import { AuthorityTypeLabel } from '../components/AuthorityTypeCard';
import ProposalDetailView from '../components/ProposalDetail/ProposalDetail';
import RecordDetail from '../components/RecordDetail/RecordDetail';
import NavPaneLayout from '../components/NavPaneLayout';
import HelpTooltip from '../components/HelpTooltip';
import PaneHelpButton from '../components/PaneHelpButton';
import ScreenHelp from '../components/ScreenHelp';
import { INBOX_HELP, REVIEW_QUEUE_HELP } from '../components/screenHelpContent';
import ReviewQueueSiglaFilter from '../components/ReviewQueueSiglaFilter';
import ReviewQueueKindFilter from '../components/ReviewQueueKindFilter';
import { editUrlFor } from '../utils/editUrlFor';
import { mclPaging } from '../utils/mclPaging';
import { MEETING_REPORT_CAPABILITY } from '../utils/meetingReportAccess';
import { useMarkReviewQueueSeen } from '../hooks/useReviewQueueNewItems';
import { readSiglaSelection, writeSiglaSelection, } from '../utils/reviewQueueSiglaSelection';
import { readKindSelection, writeKindSelection } from '../utils/reviewQueueKindSelection';
var BASE_PATH = '/czna-authorities';
var PAGE_SIZE = 50;
/** CZNA-1644 — `search` carries the kind selection along, so opening a row keeps the filter. */
var detailUrlFor = function (item, queueUrl, search) {
    return "".concat(queueUrl, "/").concat(item.subject === 'PROPOSAL' ? 'proposals' : 'records', "/").concat(item.id).concat(search);
};
var ReviewQueue = function (_a) {
    var _b, _c, _d, _e, _f, _g;
    var match = _a.match, _h = _a.mode, mode = _h === void 0 ? 'all' : _h;
    var intl = useIntl();
    var stripes = useStripes();
    var history = useHistory();
    var location = useLocation();
    var ky = useOkapiKy();
    var detailMatch = useRouteMatch("".concat(match.path, "/:subject/:id"));
    var _j = useState(0), offset = _j[0], setOffset = _j[1];
    var _k = useState('WAITING_SINCE'), sortBy = _k[0], setSortBy = _k[1];
    var _l = useState(mode === 'inbox' ? 'DESC' : 'ASC'), sortOrder = _l[0], setSortOrder = _l[1];
    var isInbox = mode === 'inbox';
    var canReport = stripes.hasPerm(MEETING_REPORT_CAPABILITY);
    var titleId = isInbox ? 'ui-czna-authorities.nav.inbox' : 'ui-czna-authorities.review.queue.title';
    // CZNA-554: opening the queue the dashboard counts is what "seen" means for its flag —
    // since CZNA-1446 that is the Inbox.
    useMarkReviewQueueSeen(isInbox);
    // CZNA-1421 — the sigla selection is remembered per user, so a supervisor finds their
    // libraries chosen again after a reload or a new login.
    var userId = (_c = (_b = stripes.user) === null || _b === void 0 ? void 0 : _b.user) === null || _c === void 0 ? void 0 : _c.id;
    // The state remembers whose selection it holds: should the user change without this route
    // remounting, the next render switches to that user's own selection instead of keeping
    // the previous user's.
    var _m = useState(function () { return ({ userId: userId, selection: readSiglaSelection(userId) }); }), siglaState = _m[0], setSiglaState = _m[1];
    if (siglaState.userId !== userId) {
        setSiglaState({ userId: userId, selection: readSiglaSelection(userId) });
        setOffset(0);
    }
    var siglaSelection = siglaState.selection;
    var changeSiglaSelection = function (selection) {
        setSiglaState({ userId: userId, selection: selection });
        setOffset(0);
        writeSiglaSelection(userId, selection);
    };
    // CZNA-1644 — the kind selection is the URL's: a click, back/forward and a shared link all
    // arrive as a changed search, and any change of it starts again on the first page.
    var kindSearch = location.search;
    var kindSelection = useMemo(function () { return readKindSelection(kindSearch); }, [kindSearch]);
    var kindKey = kindSelection.join(',');
    var _o = useState(kindKey), pagedKindKey = _o[0], setPagedKindKey = _o[1];
    if (pagedKindKey !== kindKey) {
        setPagedKindKey(kindKey);
        setOffset(0);
    }
    var changeKindSelection = function (kinds) {
        history.replace({ search: writeKindSelection(location.search, kinds) });
        setOffset(0);
    };
    // One facet request per view, not per page or sort: it aggregates the whole visible queue.
    // Under the 'review-queue' prefix, so a review action refreshes its counts too; keyed by
    // the user, so one user's cached counts are never shown to the next. Each facet counts
    // under the OTHER facet's selection (CZNA-1644), never under its own.
    var siglaFacet = useQuery(['review-queue', 'siglas', mode, userId, kindSelection], function () { return fetchReviewQueueSiglas(ky, mode, kindSelection); });
    var kindFacet = useQuery(['review-queue', 'kinds', mode, userId, siglaSelection], function () { return fetchReviewQueueKinds(ky, mode, siglaSelection); });
    var _p = useQuery(['review-queue', mode, offset, sortBy, sortOrder, siglaSelection, kindSelection], function () { return (isInbox ? fetchReturnedQueue : fetchReviewQueue)(ky, {
        offset: offset,
        limit: PAGE_SIZE,
        sortBy: sortBy,
        sortOrder: sortOrder,
        sigla: siglaSelection.siglas,
        withoutSigla: siglaSelection.withoutSigla || undefined,
        kind: kindSelection,
    }); }), data = _p.data, isLoading = _p.isLoading, isError = _p.isError;
    var items = (_d = data === null || data === void 0 ? void 0 : data.items) !== null && _d !== void 0 ? _d : [];
    var total = (_e = data === null || data === void 0 ? void 0 : data.totalRecords) !== null && _e !== void 0 ? _e : 0;
    // CZNA-1191: MCL pages by page coordinates, never the queue total — see utils/mclPaging.
    var paging = mclPaging({
        totalRecords: (_f = data === null || data === void 0 ? void 0 : data.totalRecords) !== null && _f !== void 0 ? _f : null,
        hasNextPage: data === null || data === void 0 ? void 0 : data.hasNextPage,
        pageOffset: offset,
        itemCount: items.length,
    });
    var detailSubject = detailMatch === null || detailMatch === void 0 ? void 0 : detailMatch.params.subject;
    var selectedDetail = detailMatch
        && (detailSubject === 'proposals' || detailSubject === 'records')
        ? detailMatch.params
        : undefined;
    var selectedItem = selectedDetail
        ? items.find(function (item) { return item.id === selectedDetail.id; })
        : undefined;
    var closeDetail = function () { return history.push("".concat(match.url).concat(location.search)); };
    var detailPaneProps = {
        id: 'reviewQueueDetailPane',
        defaultWidth: '44%',
        paneTitle: (_g = selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.headingValue) !== null && _g !== void 0 ? _g : intl.formatMessage({ id: titleId }),
        paneSub: selectedItem ? React.createElement(AuthorityTypeLabel, { type: selectedItem.authorityType }) : undefined,
    };
    // A review action can drain the last page while its offset is still selected. Move to
    // the new last page instead of presenting a contradictory "empty queue / N total" state.
    useEffect(function () {
        if (!isLoading && data && items.length === 0 && offset > 0) {
            setOffset(total === 0 ? 0 : Math.floor((total - 1) / PAGE_SIZE) * PAGE_SIZE);
        }
    }, [data, isLoading, items.length, offset, total]);
    var columnHeader = function (column) { return (React.createElement("span", null,
        React.createElement(FormattedMessage, { id: "ui-czna-authorities.review.queue.column.".concat(column) }),
        React.createElement(HelpTooltip, { messageId: "ui-czna-authorities.review.queue.column.".concat(column, ".help"), testKey: column, idPrefix: "review-queue-column-help" }))); };
    var columnMapping = {
        subject: columnHeader('subject'),
        authorityId: columnHeader('authorityId'),
        headingValue: columnHeader('headingValue'),
        authorityType: columnHeader('authorityType'),
        reviewStage: columnHeader('reviewStage'),
        waitingReason: columnHeader('waitingReason'),
        waitingSince: columnHeader('waitingSince'),
        actions: columnHeader('actions'),
    };
    var visibleColumns = __spreadArray(__spreadArray(__spreadArray([
        'subject',
        'authorityId',
        'headingValue',
        'authorityType'
    ], (isInbox ? ['waitingReason'] : []), true), [
        'reviewStage',
        'waitingSince'
    ], false), (isInbox ? [] : ['actions']), true);
    var formatter = {
        subject: function (item) {
            // CZNA-1644 — the kind is written out (new proposal / record correction / published
            // record): a new proposal and a correction share the pencil, so the icon alone cannot
            // tell them apart. The icon stays beside the text and still tells a proposal from a record.
            return (React.createElement("span", { "data-testid": "review-subject-".concat(item.subject.toLowerCase()), style: { display: 'inline-flex', alignItems: 'center', gap: '0.25rem' } },
                React.createElement(Icon, { icon: item.subject === 'PROPOSAL' ? 'edit' : 'document', size: "medium" }),
                React.createElement("span", { "data-test-review-kind": item.kind },
                    React.createElement(FormattedMessage, { id: "ui-czna-authorities.review.kind.".concat(item.kind) }))));
        },
        authorityType: function (item) { return React.createElement(AuthorityTypeLabel, { type: item.authorityType }); },
        reviewStage: function (item) {
            var block = item.approvalBlock;
            var recordUrl = block ? "".concat(BASE_PATH, "/authorities/").concat(block.targetRecordId) : '';
            return (React.createElement(React.Fragment, null,
                React.createElement("span", { "data-test-review-queue-row": item.id },
                    React.createElement(ReviewBadge, { reviewStatus: item.reviewStage, standalone: true })),
                block && (React.createElement("div", { "data-test-awaiting-record-approval": item.id, title: block.message },
                    React.createElement(FormattedMessage, { id: "ui-czna-authorities.review.queue.awaitingRecordApproval" }),
                    ' ',
                    React.createElement("a", { href: recordUrl, onClick: function (event) {
                            // The row opens the proposal; this link opens the record instead.
                            event.preventDefault();
                            event.stopPropagation();
                            history.push(recordUrl);
                        } }, block.targetRecordAuthorityId)))));
        },
        waitingReason: function (item) { return (item.waitingReason ? (React.createElement("strong", { "data-test-waiting-reason": item.waitingReason },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.review.waitingReason.".concat(item.waitingReason) }))) : null); },
        waitingSince: function (item) { return intl.formatDate(item.waitingSince, {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        }); },
        actions: function (item) {
            var _a;
            return (React.createElement("div", null, ((_a = item.actions) === null || _a === void 0 ? void 0 : _a.includes('edit')) && (React.createElement(Button, { onClick: function (event) {
                    event.stopPropagation();
                    history.push(item.subject === 'PROPOSAL'
                        ? "".concat(BASE_PATH, "/proposals/").concat(item.id, "/edit")
                        : editUrlFor({ id: item.id, type: item.authorityType }));
                }, marginBottom0: true, "data-test-edit-action": item.id },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.review.action.edit" })))));
        },
    };
    return (React.createElement(Paneset, null,
        React.createElement(Pane, { id: "menuPane", defaultWidth: "25%", paneTitle: intl.formatMessage({ id: titleId }) },
            React.createElement(NavPaneLayout, null,
                React.createElement(AccordionSet, null,
                    React.createElement(Accordion, { id: "review-queue-kind", label: intl.formatMessage({ id: 'ui-czna-authorities.review.queue.kind.label' }), separator: false },
                        React.createElement(ReviewQueueKindFilter, { facet: kindFacet.data, isError: kindFacet.isError, selection: kindSelection, onChange: changeKindSelection })),
                    React.createElement(Accordion, { id: "review-queue-sigla", label: intl.formatMessage({ id: 'ui-czna-authorities.review.queue.sigla.label' }), separator: false },
                        React.createElement(ReviewQueueSiglaFilter, { facet: siglaFacet.data, isError: siglaFacet.isError, selection: siglaSelection, onChange: changeSiglaSelection })),
                    React.createElement(Accordion, { label: intl.formatMessage({ id: 'ui-czna-authorities.review.queue.sort.label' }), separator: false },
                        React.createElement(Select, { id: "review-queue-sort", "aria-label": intl.formatMessage({ id: 'ui-czna-authorities.review.queue.sort.label' }), value: sortBy, dataOptions: [
                                { value: 'WAITING_SINCE', label: intl.formatMessage({ id: 'ui-czna-authorities.review.queue.sort.waiting' }) },
                                { value: 'HEADING_VALUE', label: intl.formatMessage({ id: 'ui-czna-authorities.review.queue.sort.heading' }) },
                                { value: 'AUTHORITY_ID', label: intl.formatMessage({ id: 'ui-czna-authorities.review.queue.sort.id' }) },
                            ], onChange: function (event) {
                                setSortBy(event.target.value);
                                setOffset(0);
                            } }),
                        React.createElement(Select, { id: "review-queue-sort-order", label: React.createElement(FormattedMessage, { id: "ui-czna-authorities.review.queue.sort.order" }), value: sortOrder, dataOptions: [
                                { value: 'ASC', label: intl.formatMessage({ id: 'ui-czna-authorities.review.queue.sort.asc' }) },
                                { value: 'DESC', label: intl.formatMessage({ id: 'ui-czna-authorities.review.queue.sort.desc' }) },
                            ], onChange: function (event) {
                                setSortOrder(event.target.value);
                                setOffset(0);
                            } }))))),
        React.createElement(Pane, { defaultWidth: "fill", paneTitle: intl.formatMessage({ id: titleId }), 
            /* CZNA-1100 — the printable meeting report ("sjetina") entered from here, not
               from My Drafts: NK reviews here, so this is where the report belongs. CZNA-1446 —
               a supervisor reviews from the Inbox now, so it carries the report too. CZNA-1510 —
               a cataloguer gets none: see MEETING_REPORT_CAPABILITY. `from` sends the report
               back to the view it was opened from. CZNA-1509 — each view explains what it lists: both
               share this route, not what they contain. */
            lastMenu: (React.createElement(PaneMenu, null,
                React.createElement(PaneHelpButton, null,
                    React.createElement(ScreenHelp, { spec: isInbox ? INBOX_HELP : REVIEW_QUEUE_HELP })),
                canReport && (React.createElement(Button, { marginBottom0: true, onClick: function () { return history.push("".concat(BASE_PATH, "/authorities/report").concat(isInbox ? '' : '?from=review-queue')); } },
                    React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposals.report.open" }))))), paneSub: isError
                ? undefined
                : isLoading
                    ? intl.formatMessage({ id: 'ui-czna-authorities.review.queue.loading' })
                    : intl.formatMessage({ id: 'ui-czna-authorities.review.queue.count' }, { count: total }) },
            isError ? (React.createElement(MessageBanner, { type: "error" },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.review.queue.error" }))) : null,
            items.length === 0 && offset === 0 && !isLoading && !isError ? (React.createElement(Layout, { className: "padding-all-gutter", "data-test-review-queue-empty": true },
                React.createElement(FormattedMessage, { id: isInbox
                        ? 'ui-czna-authorities.review.returned.empty'
                        : 'ui-czna-authorities.review.queue.empty' }))) : null,
            items.length > 0 ? (React.createElement("div", { role: "region", "aria-label": intl.formatMessage({ id: 'ui-czna-authorities.review.queue.table.scrollLabel' }), tabIndex: 0, "data-testid": "review-queue-table-scroll", style: { height: '100%', minHeight: 0, overflowX: 'auto', width: '100%' } },
                React.createElement("div", { "data-testid": "review-queue-table-width", style: { height: '100%', minWidth: '1200px' } },
                    React.createElement(MultiColumnList, { autosize: true, virtualize: true, contentData: items, totalCount: paging.totalCount, pagingCanGoNext: paging.canGoNext, pagingType: "prev-next", pagingOffset: offset, pageAmount: PAGE_SIZE, columnMapping: columnMapping, columnWidths: __assign(__assign({ 
                            // CZNA-1644 — room for the written-out kind ("Zveřejněný záznam") next to
                            // its icon, taken from the heading; the visible widths still add up to 100%.
                            subject: '14%', authorityId: '12%', headingValue: isInbox ? '22%' : '26%', authorityType: '13%' }, (isInbox ? { waitingReason: '12%' } : {})), { reviewStage: '15%', waitingSince: '12%', actions: '8%' }), visibleColumns: visibleColumns, formatter: formatter, nonInteractiveHeaders: visibleColumns, onRowClick: function (_event, item) {
                            return history.push(detailUrlFor(item, match.url, location.search));
                        }, selectedRow: selectedDetail ? { id: selectedDetail.id } : undefined, onNeedMoreData: function (_amount, nextOffset) { return setOffset(nextOffset); } })))) : null),
        selectedDetail && selectedDetail.subject === 'proposals' ? (
        // CZNA-778 — a proposal brings its own Pane, so its actions can sit in the header.
        React.createElement(ProposalDetailView, { id: selectedDetail.id, onClose: closeDetail, paneProps: detailPaneProps })) : null,
        selectedDetail && selectedDetail.subject !== 'proposals' ? (
        // CZNA-1456 — so does a record.
        React.createElement(RecordDetail, { id: selectedDetail.id, onClose: closeDetail, paneProps: detailPaneProps })) : null));
};
export default ReviewQueue;
